import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';

/**
 * The partner programme, from the operator's side (W47).
 *
 * Every write invalidates the whole `['admin','affiliates']` subtree rather
 * than a single key: approving a partner changes the summary counts, the list
 * row and the detail at once, and a queue that disagrees with the row above
 * it is how an operator pays somebody twice.
 */
const ROOT = ['admin', 'affiliates'] as const;

export function useAffiliateSummary() {
  return useQuery({
    queryKey: [...ROOT, 'summary'],
    queryFn: () => admin.getAffiliateSummary(),
    refetchInterval: 60_000
  });
}

export function useAffiliates(opts?: { status?: string; q?: string }) {
  return useQuery({
    queryKey: [...ROOT, 'list', opts?.status ?? 'all', opts?.q ?? ''],
    queryFn: () => admin.listAffiliates({ limit: 200, ...opts })
  });
}

export function useAffiliate(id: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'detail', id],
    queryFn: () => admin.getAffiliate(id!),
    enabled: Boolean(id)
  });
}

export function useAffiliateWithdrawals(status?: string) {
  return useQuery({
    queryKey: [...ROOT, 'withdrawals', status ?? 'all'],
    queryFn: () => admin.listAffiliateWithdrawals({ limit: 200, status }),
    refetchInterval: 60_000
  });
}

export function useAffiliateSettings() {
  return useQuery({ queryKey: [...ROOT, 'settings'], queryFn: () => admin.getAffiliateSettings() });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ROOT });
}

/** approve, suspend, reactivate or close, in one hook because the pages treat them as one decision. */
export function useAffiliateAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: 'approve' | 'suspend' | 'reactivate' | 'close'; reason?: string }) => {
      switch (action) {
        case 'approve': return admin.approveAffiliate(id, reason);
        case 'suspend': return admin.suspendAffiliate(id, reason ?? '');
        case 'reactivate': return admin.reactivateAffiliate(id, reason);
        case 'close': return admin.closeAffiliate(id, reason ?? '');
      }
    },
    onSuccess: invalidate
  });
}

export function useUpdateAffiliate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof admin.updateAffiliate>[1]) => admin.updateAffiliate(id, body),
    onSuccess: invalidate
  });
}

export function useAttachReferral() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof admin.attachReferral>[1]) => admin.attachReferral(id, body),
    onSuccess: invalidate
  });
}

export function useRevokeReferral() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ referralId, reason }: { referralId: string; reason: string }) => admin.revokeReferral(referralId, reason),
    onSuccess: invalidate
  });
}

export function useAffiliateAdjustment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, amount_minor, reason }: { id: string; amount_minor: number; reason: string }) =>
      admin.adjustAffiliate(id, { amount_minor, reason }),
    onSuccess: invalidate
  });
}

export function useWithdrawalAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, action, reason, payment_reference }: {
      id: string; action: 'approve' | 'reject' | 'paid'; reason?: string; payment_reference?: string;
    }) => {
      switch (action) {
        case 'approve': return admin.approveAffiliateWithdrawal(id, reason);
        case 'reject': return admin.rejectAffiliateWithdrawal(id, reason ?? '');
        case 'paid': return admin.markAffiliateWithdrawalPaid(id, { payment_reference: payment_reference ?? '', note: reason });
      }
    },
    onSuccess: invalidate
  });
}

export function useUpdateAffiliateSettings() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: Parameters<typeof admin.updateAffiliateSettings>[0]) => admin.updateAffiliateSettings(body),
    onSuccess: invalidate
  });
}

/** Forces the accrual pass, for an operator who does not want to wait for the sweep. */
export function useRunAccrual() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (affiliateId?: string) => admin.runAffiliateAccrual(affiliateId),
    onSuccess: invalidate
  });
}
